"use client";

import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/shared/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/ui/cards/card";
import { BracketBoard } from "@/widgets/bracket/bracket-board";
import { MatchOverviewDialog } from "@/widgets/match-overview/ui/match-overview-dialog";
import { ViewerBracketView } from "@/widgets/bracket/viewer-view.dynamic";
import { DeferredMount } from "@/shared/ui/layout/deferred-mount";
import { buildBracketDataFromArchive } from "@/widgets/tournament-archives/lib/archive-bracket-adapter";
import { TournamentParticipantsGrid } from "@/widgets/tournament-detail/tournament-participants-grid";
import { TournamentSoloParticipantsGrid } from "@/widgets/tournament-detail/tournament-solo-participants-grid";
import { type PodiumEntry } from "@/widgets/tournament-detail/tournament-podium";
import { TournamentChampionStatsCard } from "@/widgets/tournament-detail/tournament-champion-stats-card";
import { TournamentStatsCoverageNote } from "@/widgets/tournament-detail/tournament-stats-coverage-note";
import { buildPodiumEntries } from "@/widgets/tournament-detail/tabs/tournament-results-tab";
import { ArenaArchiveResults } from "./arena-archive-results";
import { TournamentResultsSections } from "@/widgets/tournament-results/tournament-results-sections";
import { archiveKeys, tournamentKeys } from "@/shared/api/query-keys";
import { prefetchTournamentResults } from "@/widgets/tournament-results/prefetch-results";
import { tournamentMvpQuery } from "@/widgets/tournament-results/mvp-section";

type RosterMember = {
  userId?: string | null;
  displayName?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  role?: string | null;
  /** "GameName#TAG" основного riot-аккаунта — для драфт-карточек участников (KAN). */
  riotId?: string | null;
  /** Ранг на момент заявки в драфт (не текущий) — для драфт-карточек участников. */
  rank?: string | null;
  /** Итоговая цена (правка модератора важнее расчётной) — для драфт-карточек участников. */
  cost?: number | null;
  /** KAN-808: твич на момент турнира — факт, не ссылка. */
  twitchLogin?: string | null;
  /** KAN-808: текущий твич из профиля — единственный кликабельный. */
  twitchLoginCurrent?: string | null;
};

type TeamArchiveDto = {
  id: string;
  originalTeamId: string;
  name: string;
  tag: string;
  icon?: string | null;
  captainId: string;
  createdAt: string;
  placement?: number | null;
  roster?: RosterMember[] | null;
  stats?: {
    wins: number;
    losses: number;
    winrate: number;
  };
};

type ArchiveSnapshotDto = {
  tournamentId: string;
  snapshotVersion: number;
  bracketType: string;
  bracketSnapshot: string;
  matchesSnapshot: string;
  participantsSnapshot: string;
  /** Замороженный список участников; у архивов старше этого поля его нет. */
  registrationsSnapshot?: string | null;
};

type ArchivedMatch = {
  id: string;
  round: number;
  matchNumber: number;
  participant1Id?: string | null;
  participant2Id?: string | null;
  team1Id?: string | null;
  team2Id?: string | null;
  draftTeam1Id?: string | null;
  draftTeam2Id?: string | null;
  winnerId?: string | null;
  team1Score?: number | null;
  team2Score?: number | null;
  status?: string | null;
  completedAt?: string | null;
};

async function fetchTournamentTeamsArchive(tournamentId: string) {
  const res = await api.get<TeamArchiveDto[]>(
    `/tournament-archives/${encodeURIComponent(tournamentId)}/teams`,
    { params: { withRosters: true } },
  );
  return Array.isArray(res.data) ? res.data : [];
}

async function fetchArchiveSnapshot(tournamentId: string) {
  const res = await api.get<ArchiveSnapshotDto>(
    `/tournament-archives/${encodeURIComponent(tournamentId)}/snapshot`,
  );
  return res.data;
}

async function fetchResults(tournamentId: string) {
  const res = await api.get<{ podium?: unknown[] }>(
    `/tournaments/${encodeURIComponent(tournamentId)}/results`,
  );
  return res.data;
}

export function TournamentArchiveResults({
  tournamentId,
  tournamentType,
}: {
  tournamentId: string;
  /** Тип турнира из архива (e.g. "Arena"). Для Arena — очковый архив вместо сетки. */
  tournamentType?: string | null;
}) {
  const tUi = useTranslations("Ui");
  const tSection = useTranslations("TournamentStats.sections");

  // KAN-794: матч, открытый кликом по ячейке сетки. Диалог тот же, что на живой
  // странице турнира, — вместе с ним приезжает и разбор игры со статистикой.
  const [openedMatchId, setOpenedMatchId] = useState<string | null>(null);

  const normalizedType = String(tournamentType ?? "").toLowerCase();
  const isArena = normalizedType === "arena";
  // Формат берём из архивного tournamentType (TournamentType: Draft/Classic/
  // SoloOneVsOne/Arena, см. Tournament.cs) — им же на живой странице турнира
  // выбирают, какой вид «Участников»/«Команд» показать (tournament-detail-tabs.tsx).
  const isDraft = normalizedType === "draft";
  const isSolo = normalizedType === "soloonevsone";

  // KAN-993: блок итогов ниже ждёт команд и снимка, а его запросы — нет.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (tournamentId && !isArena) prefetchTournamentResults(queryClient, tournamentId);
  }, [queryClient, tournamentId, isArena]);

  const { data: teams, isLoading: teamsLoading } = useQuery({
    queryKey: archiveKeys.teamsWithRosters(tournamentId),
    queryFn: () => fetchTournamentTeamsArchive(tournamentId),
    enabled: Boolean(tournamentId),
    staleTime: 30_000,
  });

  const { data: snapshot, isLoading: snapshotLoading } = useQuery({
    queryKey: archiveKeys.snapshot(tournamentId),
    queryFn: () => fetchArchiveSnapshot(tournamentId),
    enabled: Boolean(tournamentId),
    staleTime: 30_000,
  });

  // KAN-1397: карточка MVP (333 px десктоп, ~760 мобайл) встаёт между пьедесталом
  // и составами. Приходила она позже команд и снимка и выталкивала составы из
  // первого экрана — CLS 0,04–0,06 на каждом заходе. Запрос прогрет в той же волне,
  // так что ожидание стоит ~250 мс, а у турнира без MVP ничего не схлопывается.
  const { isLoading: mvpLoading } = useQuery({
    ...tournamentMvpQuery(tournamentId),
    enabled: Boolean(tournamentId) && !isArena,
  });

  // Подиум берём из /results (несёт участников с аватарами для кликабельности).
  // Фолбэк на roster-снапшот, если /results недоступен.
  const { data: results } = useQuery({
    queryKey: tournamentKeys.results(tournamentId),
    queryFn: () => fetchResults(tournamentId),
    enabled: Boolean(tournamentId),
    staleTime: 15_000,
    retry: false,
  });

  const teamById = useMemo(() => {
    const map: Record<string, TeamArchiveDto> = {};
    for (const t of teams ?? []) {
      map[t.originalTeamId] = t;
    }
    return map;
  }, [teams]);

  const podiumEntries: PodiumEntry[] = useMemo(() => {
    // Приоритет — подиум из /results (с участниками/аватарами).
    const fromResults = buildPodiumEntries(
      results?.podium as Parameters<typeof buildPodiumEntries>[0],
    );
    if (fromResults.length > 0) return fromResults;

    // Фолбэк: roster-снапшот архива.
    return (teams ?? [])
      .filter((t) => t.placement === 1 || t.placement === 2 || t.placement === 3)
      .sort((a, b) => (a.placement ?? 999) - (b.placement ?? 999))
      .map((t) => ({
        place: t.placement as number,
        name: t.name,
        tag: t.tag,
        score: t.stats ? `${t.stats.wins} : ${t.stats.losses}` : null,
        members: (t.roster ?? []).map((m) => ({
          userId: m.userId,
          displayName: m.displayName ?? m.username,
          avatarUrl: m.avatarUrl,
        })),
      }));
  }, [results, teams]);

  const matchesByRound = useMemo(() => {
    const raw = snapshot?.matchesSnapshot;
    if (!raw) return [] as Array<{ round: number; matches: ArchivedMatch[] }>;
    let parsed: ArchivedMatch[] = [];
    try {
      parsed = JSON.parse(raw) as ArchivedMatch[];
    } catch {
      parsed = [];
    }

    const byRound = new Map<number, ArchivedMatch[]>();
    for (const m of parsed) {
      const r = Number(m.round ?? 0) || 0;
      const arr = byRound.get(r) ?? [];
      arr.push(m);
      byRound.set(r, arr);
    }

    return [...byRound.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([round, matches]) => ({
        round,
        matches: matches
          .slice()
          .sort((a, b) => (a.matchNumber ?? 0) - (b.matchNumber ?? 0)),
      }));
  }, [snapshot?.matchesSnapshot]);

  // Список участников берём из снимка, а не с живой ручки: та отдаёт СЕГОДНЯШНИЙ
  // ранг игрока и по нему же группирует, поэтому архив уезжает вслед за ладдером —
  // через двое суток после турнира 19 участников из 53 уже разошлись с рангом,
  // по которому их оценивали. Снимка нет (архив старше поля) — откатываемся
  // на живую выдачу, это лучше пустой секции.
  const frozenParticipants = useMemo(() => {
    const raw = snapshot?.registrationsSnapshot;
    if (!raw) return undefined;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : undefined;
    } catch {
      return undefined;
    }
  }, [snapshot?.registrationsSnapshot]);

  // Arena — очковый формат без сетки: свой архив (пьедестал + составы + баллы).
  if (isArena) {
    return <ArenaArchiveResults tournamentId={tournamentId} />;
  }

  if (teamsLoading || snapshotLoading || mvpLoading) return null;

  const bracketData = snapshot
    ? buildBracketDataFromArchive(
        tournamentId,
        snapshot,
        (teams ?? []).map((t) => ({
          originalTeamId: t.originalTeamId,
          name: t.name,
          icon: t.icon,
        })),
      )
    : null;

  return (
    <div className="space-y-4">
      {/* KAN-878: секции итогов — общая сборка с живой вкладкой. Раньше здесь была
          копия разметки, и после редизайна страница стала выглядеть по-разному до и
          после архивации (жалоба KAN-787). Архив отдаёт замороженный снимок, живой
          турнир — живые ручки; к общему виду данные приводит каждая страница у себя. */}
      <TournamentResultsSections
        tournamentId={tournamentId}
        podiumEntries={podiumEntries}
        teams={teams ?? undefined}
      />

      {/* Всё ниже итогов монтируется на подходе к экрану (KAN-1243): сетка матчей
          меряет свою геометрию в layout-эффекте, а сетка участников драфта — это
          ~1,7 тыс. узлов; вместе с первым экраном они давали задачу в секунды. */}
      <DeferredMount minHeight={420}>
      {bracketData && bracketData.matches.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{tSection("bracketTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {/* Тот же рендер, что и на живой вкладке «Турнирная сетка»
                (tournament-bracket-tab.tsx) — владелец просил единый вид
                вместо старого ReactFlow-графа. Swiss/round-robin BracketBoard
                не умеет (падает в SE-фолбэк с фиктивными колонками), поэтому
                для них, как и на живой вкладке, оставляем ViewerBracketView.
                Правку BO и управление матчем не передаём — турнир доигран.
                А вот клик по матчу нужен (KAN-794): за ним теперь разбор игры
                со статистикой команд и составов, и именно в архиве его и
                смотрят. Раньше клика не было, и статистика была недостижима
                оттуда, откуда её ищут. */}
            {bracketData.format === "swiss" ||
            bracketData.format === "round_robin" ? (
              <ViewerBracketView data={bracketData} onMatchClick={setOpenedMatchId} />
            ) : (
              <BracketBoard
                data={bracketData}
                teamsLookup={Object.fromEntries(
                  bracketData.teams.map((t) => [t.id, t]),
                )}
                onMatchClick={setOpenedMatchId}
              />
            )}
          </CardContent>
        </Card>
      ) : matchesByRound.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{tSection("bracketTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {matchesByRound.map(({ round, matches }) => (
              <div key={round} className="space-y-3">
                <div className="text-sm font-semibold">Раунд {round}</div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {matches.map((m) => {
                    const aId =
                      m.participant1Id ?? m.team1Id ?? m.draftTeam1Id ?? "";
                    const bId =
                      m.participant2Id ?? m.team2Id ?? m.draftTeam2Id ?? "";
                    const a = aId ? teamById[aId] : undefined;
                    const b = bId ? teamById[bId] : undefined;
                    const winnerId = m.winnerId ?? "";
                    const scoreA =
                      typeof m.team1Score === "number" ? m.team1Score : 0;
                    const scoreB =
                      typeof m.team2Score === "number" ? m.team2Score : 0;
                    const aWin = winnerId && aId && winnerId === aId;
                    const bWin = winnerId && bId && winnerId === bId;

                    return (
                      <div key={m.id} className="rounded-lg border p-3">
                        <div className="text-xs text-muted-foreground mb-2">
                          Матч {m.matchNumber}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div
                              className={`min-w-0 truncate ${aWin ? "font-semibold" : ""}`}
                              title={a?.name ?? aId ?? "—"}
                            >
                              {a?.name ?? (aId ? aId : "—")}
                              {a?.tag ? (
                                <span className="text-muted-foreground"> {a.tag}</span>
                              ) : null}
                            </div>
                            <div className="text-sm font-semibold tabular-nums">
                              {scoreA}
                            </div>
                          </div>
                          <div className="flex items-center justify-between gap-2">
                            <div
                              className={`min-w-0 truncate ${bWin ? "font-semibold" : ""}`}
                              title={b?.name ?? bId ?? "—"}
                            >
                              {b?.name ?? (bId ? bId : "—")}
                              {b?.tag ? (
                                <span className="text-muted-foreground"> {b.tag}</span>
                              ) : null}
                            </div>
                            <div className="text-sm font-semibold tabular-nums">
                              {scoreB}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
      </DeferredMount>

      <TournamentStatsCoverageNote tournamentId={tournamentId} />
      <DeferredMount minHeight={320}>
        <TournamentChampionStatsCard tournamentId={tournamentId} />
      </DeferredMount>
      {/* KAN-877: таблица игроков вместо прежнего борда. Она же навигация — строка
          выбирает игрока, чей профиль появится ниже (KAN-876). До появления профиля
          выбор никуда не ведёт, поэтому страницу нельзя выкатывать на тест раньше
          KAN-876: прежний борд раскрывал радар прямо в строке, и без профиля этот
          разбор просто исчезнет с архива. */}
      {/* KAN-787: тот же блок, что и на вкладке «Итоги» живого турнира.
          Архив — не урезанная копия итогов, а их продолжение: турнир доигран, и
          разбор читают именно теперь. Раньше блок жил только в results-tab, и топы
          пропадали ровно в момент архивации, то есть когда их и начинают смотреть.
          Порядок сохранён: ниже таблицы перформанса, потому что это её
          интерпретация, а не замена (обоснование из KAN-728). Блок самопрячущийся —
          у турниров без реплеев пустой карточки не появится. */}
      {/* KAN-876: профиль выбранного игрока. Выбор живёт на странице, а не внутри
          секции: его поднимают и таблица игроков, и пьедестал, и карточка команды —
          иначе четыре секции спорили бы за то, кто показан. */}
      {/* KAN-792: командный срез. По той же причине, что и топы выше (KAN-787),
          он обязан быть и в архиве: реплеи доезжают после матчей, и командную
          статистику смотрят уже на доигранном турнире. Блок самопрячущийся. */}

      {/* Нижний блок «Участники» — вид зависит от формата турнира.
          - Draft: ТОТ ЖЕ компонент, что и на живой вкладке «Участники» турнира.
            Владелец просил вид один в один, а не собственную карточку. Он сам
            ходит в публичный /tournaments/{id}/participants и показывает очки,
            ранг, роли и короны назначенных капитанов. draftCompleted намеренно
            true: турнир в архиве всегда доигран, короны показывать уже можно.
            Модерации здесь нет по определению — isManager/isCaptain не передаём.
            Раньше тут безусловно рендерилась solo-сетка: она рассчитана только
            на 1v1 (её собственный комментарий «Участники (1v1)»), и в драфте
            показывала карточки без ранга и очков.
          - SoloOneVsOne: та же solo-сетка, для 1v1 она и задумывалась.
          - Classic и остальное: отдельного списка не показываем — «Составы
            команд» выше перечисляет тех же людей по командам, второй список
            был бы дублированием без пользы. */}
      <DeferredMount minHeight={800}>
      {isDraft ? (
        <Card>
          <CardHeader>
            <CardTitle>{tUi("tournamentArchiveResults.uchastniki")}</CardTitle>
          </CardHeader>
          <CardContent>
            <TournamentParticipantsGrid
              tournamentId={tournamentId}
              draftCompleted
              participants={frozenParticipants}
            />
          </CardContent>
        </Card>
      ) : isSolo ? (
        <Card>
          <CardHeader>
            <CardTitle>{tUi("tournamentArchiveResults.uchastniki")}</CardTitle>
          </CardHeader>
          <CardContent>
            <TournamentSoloParticipantsGrid tournamentId={tournamentId} />
          </CardContent>
        </Card>
      ) : null}
      </DeferredMount>

      <MatchOverviewDialog
        tournamentId={tournamentId}
        matchId={openedMatchId}
        onClose={() => setOpenedMatchId(null)}
      />
    </div>
  );
}
